import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchLatestResult, getGame, triggerRun } from '../services/games';
import { rankCell } from '../components/RankCell';
import { competitorRank } from '../utils/domain';
import {
  GAMEWITH_HOSTS,
  LANGUAGE_LABELS,
  googleSearchUrl,
  intervalLabel,
  type GameSettings,
  type GamewithHost,
  type KeywordResult,
  type RunResult,
  type SupportedLanguage,
} from '../types';

/** `c:<domain>` sorts by a competitor domain's rank. */
type SortKey = 'score' | 'keyword' | GamewithHost | `c:${string}`;
type SortDir = 'asc' | 'desc';

function getSortValue(r: KeywordResult, key: SortKey): number | string {
  if (key === 'score') return r.score;
  if (key === 'keyword') return r.displayKeyword || r.keyword;
  if (key.startsWith('c:')) return competitorRank(r, key.slice(2)) ?? Number.POSITIVE_INFINITY;
  return r.gamewithRankings[key as GamewithHost] ?? Number.POSITIVE_INFINITY;
}

function prevRank(r: KeywordResult, host: GamewithHost): number | null | undefined {
  return r.previousGamewithRankings ? r.previousGamewithRankings[host] : undefined;
}

/** undefined (no badge) unless both this run and the base point tracked the
 *  domain — a rank derived from topResults has no recorded previous day. */
function prevCompetitorRank(r: KeywordResult, domain: string): number | null | undefined {
  if (r.competitorRankings?.[domain] === undefined) return undefined;
  return r.previousCompetitorRankings?.[domain];
}

function ResultsTable({
  results,
  language,
  gameId,
  competitorDomains,
  showScore = true,
}: {
  results: KeywordResult[];
  language: SupportedLanguage;
  /** For per-keyword rank-history links. */
  gameId: string;
  /** The game's current competitor domains — one column each. */
  competitorDomains: string[];
  /** Custom keywords aren't scored — hide the column and sort by keyword. */
  showScore?: boolean;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>(
    showScore ? { key: 'score', dir: 'desc' } : { key: 'keyword', dir: 'asc' }
  );
  // JA keywords are already in Japanese; the translation column adds nothing.
  const showTranslation = language !== 'ja';

  const sorted = useMemo(() => {
    const arr = [...results];
    arr.sort((a, b) => {
      const va = getSortValue(a, sort.key);
      const vb = getSortValue(b, sort.key);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [results, sort]);

  const sortBy = (key: SortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  if (results.length === 0) {
    return <div className="empty card">この言語のデータはまだありません。</div>;
  }

  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th onClick={() => sortBy('keyword')}>キーワード{arrow('keyword')}</th>
            {showTranslation && <th>日本語訳</th>}
            {showScore && <th onClick={() => sortBy('score')}>スコア{arrow('score')}</th>}
            {GAMEWITH_HOSTS.map((host) => (
              <th key={host} onClick={() => sortBy(host)}>{host}{arrow(host)}</th>
            ))}
            {competitorDomains.map((d) => (
              <th key={d} className="competitor-col" onClick={() => sortBy(`c:${d}`)} title="他社ドメイン">
                {d}{arrow(`c:${d}`)}
              </th>
            ))}
            <th>検索結果へのリンク</th>
            <th>履歴</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            // Prefer the prefix-stripped label so the meaningful tail ("tier list")
            // is what the user reads first. Old run JSON without displayKeyword
            // falls back to the full Serper suggestion.
            const label = r.displayKeyword || r.keyword;
            // Has anything worth expanding? Either multiple sources, or the
            // displayed label is shorter than the original keyword (= a prefix
            // got stripped and the full form is worth revealing).
            const hasDetail = (r.sources && r.sources.length > 0) || label !== r.keyword;
            return (
              <tr key={r.keyword}>
                <td>
                  {hasDetail ? (
                    <details className="kw-details">
                      <summary>
                        <span className="kw-label">{label}</span>
                        <span className="kw-chevron">▶</span>
                      </summary>
                      <div className="kw-detail-body">
                        {(r.sources ?? []).map((s, idx) => (
                          <div key={`${s.base}-${idx}`} className="kw-source">
                            <span className="tag">{s.base}</span>
                            <span className="kw-source-keyword">{s.keyword}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <span className="kw-label">{label}</span>
                  )}
                </td>
                {showTranslation && (
                  <td className="muted">{r.translatedKeyword ?? <span className="empty">—</span>}</td>
                )}
                {showScore && <td className="score">{r.score.toFixed(2)}</td>}
                {GAMEWITH_HOSTS.map((host) => (
                  <td key={host}>{rankCell(r.gamewithRankings[host], prevRank(r, host))}</td>
                ))}
                {competitorDomains.map((d) => (
                  <td key={d} className="competitor-col">
                    {rankCell(competitorRank(r, d), prevCompetitorRank(r, d))}
                  </td>
                ))}
                <td>
                  <a
                    className="btn-secondary btn-small"
                    href={googleSearchUrl(r.keyword, language)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="順位は時間帯により変動するため、ツールの順位と一致しない場合があります"
                  >
                    Googleで開く
                  </a>
                </td>
                <td>
                  <Link
                    className="history-link"
                    to={`/games/${gameId}/history/${language}/${encodeURIComponent(r.keyword)}`}
                    title="順位履歴を見る"
                  >
                    📈
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function GameDetailPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<GameSettings | null>(null);
  const [activeLang, setActiveLang] = useState<SupportedLanguage | null>(null);
  const [resultsByLang, setResultsByLang] = useState<Record<string, RunResult | null>>({});
  const [loadingLang, setLoadingLang] = useState<SupportedLanguage | null>(null);

  // Fire-and-forget: confirm once with a cost/time warning, kick the callable
  // without awaiting, and immediately tell the user it started. The long-running
  // pipeline reports failure in Cloud Functions logs.
  const runNow = () => {
    if (!gameId || !game) return;
    const ok = confirm(
      `「${game.title}」を今すぐ実行しますか？\n外部API（Serper / Gemini）を呼び出すため数分かかり、API利用料金が発生します。`
    );
    if (!ok) return;
    triggerRun(gameId).catch((err) => {
      console.error('triggerRun failed', err);
    });
    alert('実行を開始しました。完了後、ページを再読み込みすると最新の結果が表示されます。');
  };

  useEffect(() => {
    if (!gameId) return;
    getGame(gameId).then((g) => {
      setGame(g);
      if (g && g.languages.length > 0) setActiveLang(g.languages[0]);
    });
  }, [gameId]);

  useEffect(() => {
    if (!gameId || !activeLang) return;
    if (resultsByLang[activeLang] !== undefined) return;
    setLoadingLang(activeLang);
    fetchLatestResult(gameId, activeLang)
      .then((r) => setResultsByLang((m) => ({ ...m, [activeLang]: r })))
      .catch(() => setResultsByLang((m) => ({ ...m, [activeLang]: null })))
      .finally(() => setLoadingLang(null));
  }, [gameId, activeLang, resultsByLang]);

  if (!gameId) return null;
  if (!game) return <div>読み込み中…</div>;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>{game.title}</h2>
        <div className="row">
          <Link to="/" className="btn-secondary" style={{ padding: '7px 13px' }}>← トップに戻る</Link>
          <Link to={`/games/${gameId}/edit`} className="btn-secondary" style={{ padding: '7px 13px' }}>設定変更</Link>
          {activeLang && (
            <Link to={`/games/${gameId}/analyze/${activeLang}`} className="btn-secondary" style={{ padding: '7px 13px' }}>
              AI分析
            </Link>
          )}
          <button className="primary" onClick={runNow}>今すぐ実行</button>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 24 }}>
          <div><span className="muted">取得する最大キーワード数: </span>{game.maxKeywords}</div>
          <div><span className="muted">対応言語: </span>{game.languages.map((l) => LANGUAGE_LABELS[l]).join(', ')}</div>
          <div><span className="muted">更新頻度: </span>{intervalLabel(game.intervalDays ?? 1)}（09:00 JST）</div>
          <div><span className="muted">状態: </span>{game.enabled ? '有効' : '無効'}</div>
          {(game.competitorDomains ?? []).length > 0 && (
            <div><span className="muted">他社ドメイン: </span>{game.competitorDomains!.join(', ')}</div>
          )}
        </div>
      </div>

      <div className="tabs">
        {game.languages.map((lang) => (
          <div
            key={lang}
            className={`tab ${activeLang === lang ? 'active' : ''}`}
            onClick={() => setActiveLang(lang)}
          >
            {LANGUAGE_LABELS[lang]}
          </div>
        ))}
      </div>

      {activeLang && (
        loadingLang === activeLang ? (
          <div className="card">読み込み中…</div>
        ) : resultsByLang[activeLang] ? (
          (() => {
            const run = resultsByLang[activeLang]!;
            const custom = run.customResults ?? [];
            const competitorDomains = game.competitorDomains ?? [];
            // Headings only make sense when both sections are on screen.
            if (custom.length === 0) {
              return <ResultsTable results={run.results} language={activeLang} gameId={gameId} competitorDomains={competitorDomains} />;
            }
            return (
              <>
                <h3 style={{ marginBottom: 8 }}>指定ワード</h3>
                <ResultsTable
                  results={custom}
                  language={activeLang}
                  gameId={gameId}
                  competitorDomains={competitorDomains}
                  showScore={false}
                />
                <h3 style={{ marginBottom: 8, marginTop: 24 }}>自動選定キーワード</h3>
                {run.results.length > 0 ? (
                  <ResultsTable results={run.results} language={activeLang} gameId={gameId} competitorDomains={competitorDomains} />
                ) : (
                  // ResultsTable's own empty card says the whole language has
                  // no data — wrong here, where custom rows sit right above.
                  <div className="empty card">自動選定キーワードはまだありません。</div>
                )}
              </>
            );
          })()
        ) : (
          <div className="empty card">
            この言語のデータはまだありません。「今すぐ実行」を押して取得してください。
          </div>
        )
      )}
    </>
  );
}
